import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SynapseService } from 'src/app/core/services/synapse';
import * as fromStore from 'src/app/store/';
import {
  HistoricalMetricData,
  NetworkMetrics,
} from '../../metric-details.model';

@Component({
  selector: 'app-metric-table-component',
  templateUrl: './metric-table.component.html',
  standalone: false,
})
export class MetricTableComponent implements OnInit, OnDestroy {
    
  private tableDataRequestId = 0;
  
  private metricSubscription: Subscription;

  private aggregatedMetricsSubscription: Subscription;

  private destroy$ = new Subject<void>();

  metricType: string = '';

  mapOfMetricNames: Map<string, string> = new Map<string, string>([
    ['data_usage', 'Data Usage (Mbps)'],
    ['data_utilization', 'Data Utilization (MB)'],
    ['jitter', 'Jitter (ms)'],
    ['latency', 'Latency (ms)'],
    ['packet_loss', 'Packet Loss (%)'],
  ]);

  selectedDimension: string = '';

  activationTime: string = '';

  selectedSorting: string = '';

  selectedMetricLabel: string = '';

  selectedDevice: string = '';

  selectedDeviceIndex: number = 0;

  selectedDeviceChartLabel: string = '';

  data = []; // Replace with your actual table data

  filteredData = this.data;

  accountName: string = '';

  nitelIQData: any[] = [];

  nitelIQDataAggregated: any;

  timeOfDayBreakdownData = [];

  // Top Metric data values
  sortedMetricsData = [];

  metricCategories = [];

  historicalMetricData: HistoricalMetricData[] = [];

  historicalCategories = [];

  loading: boolean = false;

  tableLoading: boolean = false;

  isNoContent: boolean = false;

  showMockData = false;

  isNoTableContent: boolean = false;

  unmTableData: MatTableDataSource<NetworkMetrics>;

  private synapseService = inject(SynapseService);

  private cdr = inject(ChangeDetectorRef);

  private store = inject(Store<fromStore.State>);

  constructor() {
    this.selectedDimension = 'device';
    this.activationTime = '1d';
    this.selectedSorting = 'DESC';
    this.metricType = 'data_usage';
  }

  ngOnInit(): void {
    if (this.accountName) {
      this.loadTableData();
      this.loadAggregatedData();
    } else {
      this.store.select(fromStore.getCurrentUser).subscribe({
        next: (data) => {
          this.accountName = data.Account?.Name;
          this.loadTableData();
          this.loadAggregatedData();
        },
        error: (err) => console.error('Error fetching user data:', err),
      });
    }
  }

  onChangeDevice(event: any): void {
    this.selectedDevice = event;
    this.getHistoricalData();
  }

  ngOnDestroy(): void {
    if (this.metricSubscription) {
      this.metricSubscription.unsubscribe();
    }
    if (this.aggregatedMetricsSubscription) {
      this.aggregatedMetricsSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  getMetricLableByName(): void {
    this.selectedMetricLabel = this.mapOfMetricNames.get(this.metricType);
  }

  // Method to handle the emitted value
  onChildSelectionChange(value: string): void {
    if (this.activationTime !== value) {
      this.activationTime = value;
      this.loadTableData();
      this.loadAggregatedData();
    }
  }

  loadTableData(): void {
    const currentRequestId = ++this.tableDataRequestId;
    this.unmTableData = new MatTableDataSource<NetworkMetrics>();
    this.tableLoading = true; // Start spinner
    this.isNoTableContent = false;
    if (this.metricSubscription) {
      this.metricSubscription.unsubscribe();
    }
    this.metricSubscription = this.synapseService
      .fetchAllMetricDetails(
        this.accountName,
        this.activationTime,
        this.selectedDimension,
        this.showMockData,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          
          if (currentRequestId !== this.tableDataRequestId) return;
          this.nitelIQData = data; // Store fetched data
          if (this.nitelIQData?.length) {
            this.getUnmTableData(); // Update metrics and charts
            this.isNoTableContent = false; // Data is available
            this.cdr.detectChanges();
          } else {
            this.isNoTableContent = true; // No data
            console.error('Empty getMetricDetails: ', this.nitelIQData);
          }
          this.tableLoading = false; // Stop spinner
        },
        error: (err) => {
          
          if (currentRequestId !== this.tableDataRequestId) return;
          this.isNoTableContent = true;
          this.tableLoading = false;
          console.error('Error fetching metrics:', err); // Log errors
        },
      });
  }

  toggleMockData(event: boolean): void {
    this.showMockData = event;
    this.ngOnInit(); // Reload data
  }

  loadAggregatedData(): void {
    this.loading = true; // Start spinner
    this.aggregatedMetricsSubscription = this.synapseService
      .getAggregatedMetrics(
        this.accountName,
        this.activationTime,
        this.selectedDimension,
        this.metricType,
        this.selectedSorting,
        this.showMockData,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.nitelIQDataAggregated = data;
          if (this.nitelIQDataAggregated) {
            this.getMetricLableByName();
            this.getMetricsData(); // Update metrics and charts
            this.isNoContent = false;
            this.loading = false;
            this.cdr.detectChanges();
          } else {
            console.error(
              'Empty getAggregatedMetrics: ',
              this.nitelIQDataAggregated?.metric_ranks.length,
            );
            this.isNoContent = true; // No data
          }
          this.cdr.detectChanges();
          this.loading = false; // Stop spinner
        },
        error: (err) => {
          this.nitelIQDataAggregated = [];
          this.sortedMetricsData = [];
          this.metricCategories = [];
          this.historicalMetricData = [];
          this.historicalCategories = [];
          this.loading = false; // Stop spinner
          this.isNoContent = true;
          this.cdr.detectChanges();
          console.error('Error fetching aggregated metrics:', err);
        },
      });
  }

  onMetricChange() {
    this.getMetricLableByName();
    this.loadAggregatedData();
  }

  onSortingChange(value: string) {
    if (this.selectedSorting !== value) {
      this.selectedSorting = value;
      this.loadAggregatedData();
      this.loadTableData();
    }
  }

  onDimensionChange(value: string) {
    if (this.selectedDimension !== value) {
      this.selectedDimension = value;
      if (this.selectedDimension !== 'connection') {
        this.metricType = 'data_usage';
      }
      this.loadAggregatedData();
      this.loadTableData();
    }
  }

  getTimeOfDayBreakdownData(): void {
    let firstTimeFrame = 0;
    let secondTimeFrame = 0;
    let thirdTimeFrame = 0;

    for (const key in this.nitelIQData) {
      const currentItem = this.nitelIQData[key];
      const { time_frame } = currentItem;

      const date = new Date(time_frame);
      const hours = date.getUTCHours();

      if (hours >= 0 && hours < 8) {
        firstTimeFrame++;
      } else if (hours >= 8 && hours < 16) {
        secondTimeFrame++;
      } else if (hours >= 16 && hours <= 23) {
        thirdTimeFrame++;
      }
    }
    this.timeOfDayBreakdownData = [
      firstTimeFrame,
      secondTimeFrame,
      thirdTimeFrame,
    ];
  }

  getMetricsData(): void {
    const deviceNameMap = new Map();
    const timeValuesMap = new Map();

    const containsItem = this.nitelIQDataAggregated?.metric_ranks.filter(
      (device) => this.selectedDevice === device.deviceName,
    );

    for (const key in this.nitelIQDataAggregated?.metric_ranks) {
      const currentItem = this.nitelIQDataAggregated?.metric_ranks[key];

      deviceNameMap.set(currentItem.dimensionName, currentItem.metric_value);

      for (const index in currentItem.trend) {
        const historicalMetric =
          timeValuesMap.get(currentItem.dimensionName) || [];
        historicalMetric.push({
          time_frame: this.formatDate(currentItem.trend[index].date),
          metric_value: currentItem.trend[index].value,
        });
        timeValuesMap.set(currentItem.dimensionName, historicalMetric);
      }
    }

    const dataForChart = [];
    const categoriesForChart = [];

    for (const [key, value] of deviceNameMap) {
      if (dataForChart.length < 10) {
        dataForChart.push(Number(value));
        categoriesForChart.push(key);
      }
    }

    if (this.selectedSorting === 'ASC' && this.showMockData === true) {
      dataForChart.reverse();
      categoriesForChart.reverse();
    }

    this.sortedMetricsData = [
      {
        name: this.selectedMetricLabel,
        data: dataForChart,
      },
    ];

    if (!this.selectedDevice || !containsItem.length) {
      this.selectedDevice = categoriesForChart[0];
      this.selectedDeviceIndex = 0;
    }

    this.metricCategories = categoriesForChart;
    this.getHistoricalData();
  }

  getHistoricalData(): void {
    for (const key in this.nitelIQDataAggregated?.metric_ranks) {
      const currentItem = this.nitelIQDataAggregated?.metric_ranks[key];

      if (currentItem.dimensionName === this.selectedDevice) {
        const historicalData = [];
        const historicalCategories = [];
        for (const index in currentItem.trend) {
          const dateStr = currentItem.trend[index].date;
          const m = dateStr.endsWith('Z')
            ? moment(dateStr)
            : moment.utc(dateStr.replace(' ', 'T'));
          const localTimestamp =
            this.activationTime === '1d'
              ? m.local().valueOf() - new Date().getTimezoneOffset() * 60 * 1000
              : m.local().valueOf();
          historicalCategories.push([
            localTimestamp,
            currentItem.trend[index].value,
          ]);
        }

        this.historicalMetricData = [
          {
            name: this.selectedMetricLabel,
            data: historicalData,
          },
        ];
        this.historicalCategories = historicalCategories;
        const namePrefix =
          this.selectedDimension === 'connection'
            ? this.mapOfMetricNames.get(this.metricType)
            : this.mapOfMetricNames.get('data_usage');
        this.selectedDeviceChartLabel = `${namePrefix} for ${this.selectedDevice}`;
      }
    }
  }

  getUnmTableData(): void {
    const networkMetrics: any[] = [];

    for (const key in this.nitelIQData) {
      const currentItem = this.nitelIQData[key];
      const { metrics } = currentItem;

      const date = new Date(currentItem.time_frame);

      const tableItem = {
        account: currentItem.account,
        account_id: currentItem.account_id,
        carrier: currentItem.carrier,
        source: currentItem.source,
        device_id: currentItem.device_id,
        device_name: currentItem.device_name,
        time_frame: this.formatDate(date.toString()),
        bucket: currentItem.bucket,
        application: currentItem.application,
        link_name: `${currentItem.device_name}-${currentItem.connection}`,
      };

      metrics.forEach((metric) => {
        tableItem[metric.metric_type] = metric.metric_value;
      });

      networkMetrics.push(tableItem);
    }

    this.unmTableData = new MatTableDataSource(networkMetrics);
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);

    // Format date as MM/DD/YYYY
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });

    // Format time as H:MM AM/PM
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      second: 'numeric',
    });

    return `${formattedDate} ${formattedTime}`;
  }
}
